import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import ReactPlayer from "react-player";
import Vimeo from "@u-wave/react-vimeo";

import Wrapper from "../../components/Wrapper";
import Title from "../../components/Title";
import { baseConfig } from "../../utils/config";
import { Context } from "../../contexts/Store";
import DataTable from "../../components/DataTable/DataTable";
import TopicAddModal from "./components/TopicAddModal";
import ContentAddModal from "./components/ContentAddModal";
import deleteIcon from "../../assets/images/delete.svg";
import loadingGif from "../../assets/images/loading-buffering.gif";
import DeleteModal from "../../components/includes/DeleteModal";

export const Loading = () => (
    <LoadingContainer>
        <LoadingGif src={loadingGif} alt="Loading" />
    </LoadingContainer>
);

const Breadcrumb = ({
    title,
    contentTitle,
    navigateToCoursesPage,
    onContentTitleClick,
}) => (
    <TextDiv>
        <PathText onClick={navigateToCoursesPage}>Course details</PathText>
        <Slash>/</Slash>
        {contentTitle ? (
            <>
                <PathText
                    onClick={contentTitle ? () => onContentTitleClick() : null}
                >
                    {title ?? "--"}
                </PathText>
                <Slash>/</Slash>
                <PathTextBlack>{contentTitle}</PathTextBlack>
            </>
        ) : (
            <PathTextBlack
                onClick={contentTitle ? () => onContentTitleClick() : null}
            >
                {title ?? "--"}
            </PathTextBlack>
        )}
    </TextDiv>
);

const CourseImage = ({ image }) => (
    <TopSection>
        <ImageDiv>
            <img src={image} alt="course-image" />
        </ImageDiv>
    </TopSection>
);

const CourseHeader = ({ category, isPublished, onPublishToggle }) => (
    <HeaderSection>
        <SikillBox>
            {typeof category === "object" ? category.name : category}
        </SikillBox>
        <PublishToggle>
            <PublishText>{isPublished ? "Published" : "Publish"}</PublishText>
            <ToggleRoutes
                onClick={() => {
                    if (!isPublished) {
                        onPublishToggle();
                    }
                }}
                checked={isPublished}
            />
        </PublishToggle>
    </HeaderSection>
);

const Instructor = ({ instructor }) => (
    <InstructorSection>
        <InstructorLabel>Instructor</InstructorLabel>
        <InstructorDetail>
            <InstructorAvatar>
                <img src={instructor?.image} alt="instructor" />
            </InstructorAvatar>
            <InstructorName>{instructor?.name}</InstructorName>
        </InstructorDetail>
    </InstructorSection>
);

const Tabs = ({ activeTab, setActiveTab }) => (
    <TabsContainer>
        <Tab
            active={activeTab === "About"}
            onClick={() => setActiveTab("About")}
        >
            About
        </Tab>
        <Tab
            active={activeTab === "Enrolled Students"}
            onClick={() => setActiveTab("Enrolled Students")}
        >
            Enrolled Students
        </Tab>
    </TabsContainer>
);

const AboutSectionComponent = ({ description, skills }) => (
    <AboutSection>
        <SectionTitle>About Course</SectionTitle>
        <Description>{description}</Description>

        <SectionTitle>Skills you will learn</SectionTitle>
        <SkillsContainer>
            {Array.isArray(skills) &&
                skills.map((skill, index) => (
                    <SkillBadge key={index}>
                        {typeof skill === "object" ? skill.name : skill}
                        {/* <CloseIcon>×</CloseIcon> */}
                    </SkillBadge>
                ))}
        </SkillsContainer>
    </AboutSection>
);

const ContentDetailComponent = ({
    content,
    setIsContent,
    setContentType,
    setContentEditId,
}) => (
    <ContentDetailView>
        <MediaContainer>
            <ReactPlayer
                id="media-container"
                className="vid-player"
                url={content.video_link}
                controls={true}
                width="100%"
                config={{
                    youtube: {
                        playerVars: {
                            autoplay: 1,
                            rel: 0,
                            modestbranding: 1,
                            autoplay: 1,
                            loop: 1,
                        },
                    },
                    vimeo: {
                        playerVars: {
                            controls: false,
                        },
                        playerOptions: {},
                        id: "vimeo-player-new-id",
                    },
                }}
            />
        </MediaContainer>
        <ContentDetailTitle>
            {content.title}
            <ContentEdit
                onClick={() => {
                    setContentEditId(content);
                    setIsContent(true);
                    setContentType("edit");
                }}
            >
                Edit Content
            </ContentEdit>
        </ContentDetailTitle>

        <div>
            <h4>About Lesson</h4>
            <ContentDetailDescription>
                {content.description || "No description available."}
            </ContentDetailDescription>
        </div>
    </ContentDetailView>
);

const ContentItemComponent = ({
    content,
    index,
    onContentClick,
    setDeleteId,
    setDeleteModal,
}) => (
    <ContentItem key={content.id}>
        <ContentRow>
            <ContentThumbnail
                bgImage={content?.thumbnail}
                onClick={() => onContentClick(content)}
            >
                {content.content_type === "video" ? (
                    <PlayIcon />
                ) : (
                    <DocumentIcon />
                )}
            </ContentThumbnail>
            <ContentInfo>
                <ContentLesson>
                    Lesson {index + 1} | {content.duration || "20:09"}
                </ContentLesson>
                <ContentTitle>{content.title}</ContentTitle>
                <ContentDescription>
                    {content.description?.slice(0, 40) ||
                        "Lorem ipsum dolor sit amet..."}
                </ContentDescription>
            </ContentInfo>
            <ContentActions>
                <DeleteIcon
                    onClick={() => {
                        setDeleteId(content.id);
                        setDeleteModal(true);
                    }}
                >
                    <img src={deleteIcon} alt="delete" />
                </DeleteIcon>
            </ContentActions>
        </ContentRow>
    </ContentItem>
);

const TopicsSection = ({
    title,
    topics,
    expandedTopic,
    toggleTopic,
    handleAddTopic,
    handleAddContent,
    handleContentClick,
    setDeleteId,
    setDeleteModal,
    setTopicDeleteId,
    setTopicDeleteModal,
    setTopicData,
    setEditTopic,
    setModal,
    setTopicId,
}) => (
    <Right>
        <TopicsHeader>
            <TopicsTitle>{title}</TopicsTitle>
            <AddTopicButton onClick={handleAddTopic}>
                <AddIcon>+</AddIcon> Add{" "}
                {title === "Topics" ? "Topics" : "Content"}
            </AddTopicButton>
        </TopicsHeader>
        <TopicsContainer>
            {topics && topics.length > 0 ? (
                topics.map((topic) => (
                    <TopicItem key={topic.id}>
                        <TopicHeader onClick={() => toggleTopic(topic.id)}>
                            <TopicName>{topic.title}</TopicName>
                            <ExpandIcon>
                                {expandedTopic === topic.id ? "▲" : "▼"}
                            </ExpandIcon>
                        </TopicHeader>
                        {expandedTopic === topic.id && (
                            <TopicContent>
                                {topic.contents && topic.contents.length > 0 ? (
                                    <>
                                        <ContentButtons
                                            onClick={() => {
                                                handleAddContent(topic.id);
                                                setTopicId(topic.id);
                                            }}
                                        >
                                            Add Content
                                        </ContentButtons>
                                        <ContentButtons
                                            onClick={() => {
                                                setTopicData(topic);
                                                setEditTopic("edit");
                                                setModal(true);
                                            }}
                                        >
                                            Edit Topic
                                        </ContentButtons>
                                        <ContentButtons
                                            onClick={() => {
                                                setTopicDeleteId(topic.id);
                                                setTopicDeleteModal(true);
                                            }}
                                            style={{ color: "#B3261E" }}
                                        >
                                            Delete
                                        </ContentButtons>
                                        {topic.contents.map(
                                            (content, index) => (
                                                <ContentItemComponent
                                                    key={content.id}
                                                    content={content}
                                                    index={index}
                                                    onContentClick={
                                                        handleContentClick
                                                    }
                                                    setDeleteId={setDeleteId}
                                                    setDeleteModal={
                                                        setDeleteModal
                                                    }
                                                />
                                            )
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <ContentButtons
                                            onClick={() => {
                                                setTopicData(topic);
                                                setEditTopic("edit");
                                                setModal(true);
                                            }}
                                        >
                                            Edit Topic
                                        </ContentButtons>
                                        <ContentButtons
                                            onClick={() => {
                                                setTopicDeleteId(topic.id);
                                                setTopicDeleteModal(true);
                                            }}
                                            style={{ color: "#B3261E" }}
                                        >
                                            Delete
                                        </ContentButtons>
                                        <AddContentSection>
                                            <AddContentText>
                                                Add a Content to this Topic
                                            </AddContentText>
                                            <AddContentButton
                                                onClick={() => {
                                                    handleAddContent(topic.id);
                                                    setTopicId(topic.id);
                                                }}
                                            >
                                                <AddIcon>+</AddIcon> Add Content
                                            </AddContentButton>
                                        </AddContentSection>
                                    </>
                                )}
                            </TopicContent>
                        )}
                    </TopicItem>
                ))
            ) : (
                <></>
            )}
        </TopicsContainer>
    </Right>
);

const ContentsListSection = ({
    contents,
    handleAddContent,
    handleContentClick,
    setDeleteId,
    setDeleteModal,
}) => (
    <Right>
        <TopicsHeader>
            <TopicsTitle>Contents</TopicsTitle>
            <AddTopicButton onClick={() => handleAddContent()}>
                <AddIcon>+</AddIcon> Add Content
            </AddTopicButton>
        </TopicsHeader>
        <TopicsContainer>
            {contents && contents.length > 0 ? (
                contents.map((content, index) => (
                    <ContentItemComponent
                        key={content.id}
                        content={content}
                        index={index}
                        onContentClick={handleContentClick}
                        setDeleteId={setDeleteId}
                        setDeleteModal={setDeleteModal}
                    />
                ))
            ) : (
                <></>
            )}
        </TopicsContainer>
    </Right>
);

export default function CourseView() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { state } = useContext(Context);
    const [loading, setLoading] = useState(false);
    const [courses, setCourses] = useState([]);
    const [activeTab, setActiveTab] = useState("About");
    const [published, setPublished] = useState(false);
    const [students, setStudents] = useState([]);
    const [isModal, setIsModal] = useState(false);
    const [reload, setReload] = useState(false);
    const [expandedTopic, setExpandedTopic] = useState(null);
    const [isContent, setIsContent] = useState(false);
    const [topicId, setTopicId] = useState(null);
    const [selectedContent, setSelectedContent] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [isDeleteModal, setDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState("");
    const [topicDeleteId, setTopicDeleteId] = useState("");
    const [isTopicDeleteModal, setTopicDeleteModal] = useState(false);
    const [contentType, setContentType] = useState(null);
    const [contentEditId, setContentEditId] = useState(null);
    const [editTopic, setEditTopic] = useState(null);
    const [TopicData, setTopicData] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState("");
    const [sortOrder, setSortOrder] = useState("");

    const toggleTopic = (topicId) => {
        setExpandedTopic((prev) => (prev === topicId ? null : topicId));
    };

    const handleContentClick = (content) => {
        setSelectedContent(content);
        setActiveTab("About");
    };

    useEffect(() => {
        const getCourse = async () => {
            setLoading(true);
            let accessToken = state.user_details.access_token;
            try {
                const response = await baseConfig.get(
                    `free-courses/courses/${id}`,
                    {
                        headers: {
                            Authorization: "Bearer " + accessToken,
                        },
                        params: {},
                    }
                );
                const { StatusCode, data } = response.data;
                if (StatusCode === 6000) {
                    setCourses(data);
                }
            } catch (error) {
                if (error?.response?.data?.code === "token_not_valid") {
                    localStorage.clear();
                    window.location = "/";
                }
                console.error("Error fetching course:", error);
            } finally {
                setLoading(false);
            }
        };
        getCourse();
    }, [id, state.user_details.access_token, reload]);

    const handleSort = (field, order) => {
        const formattedField = order === "desc" ? `-${field}` : field;

        const fieldMapping = {
            "student_data.name": "student__name",
            "student_data.admission_number": "student__admission_number",
            "student_data.mail": "student__mail",
            enrolled_date: "received_date",
        };

        const backendField = fieldMapping[field]
            ? order === "desc"
                ? `-${fieldMapping[field]}`
                : fieldMapping[field]
            : formattedField;

        setSortField(backendField);
        setSortOrder(order);
    };

    const handleFilterApply = (filters) => {
        // Implementation for filtering can be added here
    };

    useEffect(() => {
        const getStudents = async () => {
            let accessToken = state.user_details.access_token;
            try {
                const response = await baseConfig.get(
                    `free-courses/enrollments/${id}/`,
                    {
                        headers: {
                            Authorization: "Bearer " + accessToken,
                        },
                        params: {
                            search: searchTerm,
                            page: currentPage,
                            items: 5, // Number of items per page
                            ordering: sortField,
                        },
                    }
                );
                const { StatusCode, data } = response.data;
                if (StatusCode === 6000) {
                    setStudents(data);
                }
            } catch (error) {
                if (error?.response?.data?.code === "token_not_valid") {
                    localStorage.clear();
                    window.location = "/";
                }
                console.error("Error fetching students:", error);
            }
        };

        if (activeTab === "Enrolled Students") {
            getStudents();
        }
    }, [
        activeTab,
        searchTerm,
        currentPage,
        sortField,
        id,
        state.user_details.access_token,
    ]);

    const handlePageChange = (page, search) => {
        setCurrentPage(page);
        setSearchTerm(search);
    };

    const handleAddTopic = () => {
        setIsModal(!isModal);
        setEditTopic(null);
    };

    const handleAddContent = (id) => {
        // setTopicId(id);
        setIsContent(true);
    };

    const handlePublishToggle = async () => {
        let accessToken = state.user_details.access_token;
        try {
            const response = await baseConfig.put(
                `free-courses/publish/${id}/`,
                {},
                {
                    headers: {
                        Authorization: "Bearer " + accessToken,
                    },
                }
            );

            const { StatusCode } = response.data;
            if (StatusCode === 6000) {
                setPublished(!published);
                setCourses((prevCourses) => ({
                    ...prevCourses,
                    is_published: !published,
                }));
            }
        } catch (error) {
            if (error?.response?.data?.code === "token_not_valid") {
                localStorage.clear();
                window.location = "/";
            }
            console.error("Publish toggle error:", error);
        }
    };

    const navigateToCoursesPage = () => {
        navigate("/superadmin/free-courses/courses/");
    };

    const deleteContent = async () => {
        let accessToken = state.user_details.access_token;
        try {
            const response = await baseConfig.delete(
                `/free-courses/contents/${deleteId}/`,
                {
                    headers: {
                        Authorization: "Bearer " + accessToken,
                    },
                }
            );
            const { StatusCode } = response.data;
            if (StatusCode === 6000) {
                setReload(!reload);
                window.location.reload();
                setDeleteModal(false);
            } else {
                // toast.error("Failed to delete instructor");
            }
        } catch (error) {
            if (error?.response?.data?.code === "token_not_valid") {
                localStorage.clear();
                window.location = "/";
            }
            // toast.error(error.message);
        }
    };

    const deleteTopic = async () => {
        let accessToken = state.user_details.access_token;
        try {
            const response = await baseConfig.delete(
                `/free-courses/topics/${topicDeleteId}/`,
                {
                    headers: {
                        Authorization: "Bearer " + accessToken,
                    },
                }
            );
            const { StatusCode } = response.data;
            if (StatusCode === 6000) {
                setReload(!reload);
                window.location.reload();
                setTopicDeleteId(false);
            } else {
                // toast.error("Failed to delete instructor");
            }
        } catch (error) {
            if (error?.response?.data?.code === "token_not_valid") {
                localStorage.clear();
                window.location = "/";
            }
            // toast.error(error.message);
        }
    };

    const formatDate = (dateString) => {
        const options = { day: "2-digit", month: "short", year: "numeric" };
        return new Date(dateString).toLocaleDateString("en-GB", options);
    };

    const columns = [
        {
            header: "SL No",
            accessor: "id",
            width: "80px",
            sortable: false,
        },
        {
            header: "Admission No",
            accessor: "student_data.admission_number",
            width: "120px",
            sortable: true,
            searchable: true,
        },
        {
            header: "Name",
            cell: (row) => (
                <StudentInfo>
                    <StudentAvatar>
                        <AvatarImage
                            src={row.student_data.image}
                            alt={row.student_data.name}
                        />
                    </StudentAvatar>
                    {row.student_data.name}
                </StudentInfo>
            ),
            accessor: "student_data.name",
            width: "200px",
            sortable: true,
            searchable: true,
        },
        {
            header: "Email address",
            accessor: "student_data.mail",
            width: "200px",
            sortable: true,
            searchable: true,
        },
        {
            header: "Enrolled Date",
            cell: (row) => formatDate(row.enrolled_date),
            accessor: "enrolled_date",
            width: "150px",
            sortable: true,
        },
    ];

    const filterOptions = [
        {
            label: "Enrolled Date",
            value: "enrolled_date",
            type: "date",
        },
    ];

    return (
        <Wrapper>
            <MainContainer>
                {loading ? (
                    <Loading />
                ) : (
                    <CourceMainContainer>
                        <Left
                            hasTopics={
                                courses?.topics?.length > 0 ||
                                courses?.contents?.length > 0
                            }
                        >
                            <TopTitleWrapper>
                                <Title fontSize="35px">Courses</Title>
                                <TopicsHeader>
                                    {courses?.topics?.length === 0 ? (
                                        <AddTopicButton
                                            onClick={handleAddTopic}
                                        >
                                            <AddIcon>+</AddIcon> Add Topics
                                        </AddTopicButton>
                                    ) : courses?.contents?.length === 0 ? (
                                        <AddTopicButton
                                            onClick={handleAddContent}
                                        >
                                            <AddIcon>+</AddIcon> Add Content
                                        </AddTopicButton>
                                    ) : null}
                                </TopicsHeader>
                            </TopTitleWrapper>
                            <Breadcrumb
                                title={courses?.title}
                                contentTitle={selectedContent?.title}
                                navigateToCoursesPage={navigateToCoursesPage}
                                onContentTitleClick={() =>
                                    setSelectedContent(null)
                                }
                            />
                            <Card>
                                {selectedContent ? (
                                    <ContentDetailComponent
                                        content={selectedContent}
                                        setIsContent={setIsContent}
                                        setContentType={setContentType}
                                        setContentEditId={setContentEditId}
                                    />
                                ) : (
                                    <>
                                        <CourseImage image={courses.image} />
                                        <CourseHeader
                                            category={courses.category_data}
                                            isPublished={
                                                courses.is_published ||
                                                published
                                            }
                                            onPublishToggle={
                                                handlePublishToggle
                                            }
                                        />
                                        <MainTitle>{courses.title}</MainTitle>
                                        <Instructor
                                            instructor={courses.instructor_data}
                                        />
                                        <Tabs
                                            activeTab={activeTab}
                                            setActiveTab={setActiveTab}
                                        />

                                        {activeTab === "About" && (
                                            <AboutSectionComponent
                                                description={
                                                    courses.description
                                                }
                                                skills={
                                                    courses.skills_learn_data
                                                }
                                            />
                                        )}

                                        {activeTab === "Enrolled Students" && (
                                            <EnrolledStudentsSection>
                                                <DataTable
                                                    data={
                                                        students?.enrollments ||
                                                        []
                                                    }
                                                    columns={columns}
                                                    searchable={true}
                                                    searchPlaceholder="Search students..."
                                                    itemsPerPage={5}
                                                    paginator={
                                                        students?.paginator ||
                                                        {}
                                                    }
                                                    onPageChange={
                                                        handlePageChange
                                                    }
                                                    onSearch={setSearchTerm}
                                                    onSort={handleSort}
                                                    title="Enrolled Students"
                                                    pageCount={
                                                        students?.paginator
                                                            ?.count || 0
                                                    }
                                                    filterOptions={
                                                        filterOptions
                                                    }
                                                    onFilterApply={
                                                        handleFilterApply
                                                    }
                                                />
                                            </EnrolledStudentsSection>
                                        )}
                                    </>
                                )}
                            </Card>
                        </Left>
                        {courses?.lecture_type === "topic" &&
                            courses?.topics?.length > 0 && (
                                <TopicsSection
                                    title="Topics"
                                    topics={courses?.topics}
                                    expandedTopic={expandedTopic}
                                    toggleTopic={toggleTopic}
                                    handleAddTopic={handleAddTopic}
                                    handleAddContent={handleAddContent}
                                    handleContentClick={handleContentClick}
                                    setDeleteId={setDeleteId}
                                    setDeleteModal={setDeleteModal}
                                    setTopicDeleteId={setTopicDeleteId}
                                    setTopicDeleteModal={setTopicDeleteModal}
                                    setTopicData={setTopicData}
                                    setEditTopic={setEditTopic}
                                    setModal={setIsModal}
                                    setTopicId={setTopicId}
                                />
                            )}
                        {courses?.lecture_type === "content" &&
                            courses.contents.length > 0 && (
                                <ContentsListSection
                                    contents={courses?.contents}
                                    handleAddContent={handleAddContent}
                                    handleContentClick={handleContentClick}
                                    setDeleteId={setDeleteId}
                                    setDeleteModal={setDeleteModal}
                                />
                            )}
                    </CourceMainContainer>
                )}
            </MainContainer>

            {isModal && (
                <TopicAddModal
                    isOpen={isModal}
                    onClose={() => setIsModal(false)}
                    setReload={setReload}
                    reload={reload}
                    slug={id}
                    editTopic={editTopic}
                    TopicData={TopicData}
                />
            )}

            {isContent && (
                <ContentAddModal
                    isOpen={isContent}
                    onClose={() => {
                        setIsContent(false);
                        setContentType(null);
                    }}
                    setReload={setReload}
                    reload={reload}
                    slug={id}
                    topicId={topicId}
                    contentType={contentType}
                    contentEditId={contentEditId}
                />
            )}

            {isDeleteModal && (
                <DeleteModal
                    isModal={isDeleteModal}
                    setModal={setDeleteModal}
                    deleteId={deleteId}
                    handleDeleteData={deleteContent}
                />
            )}
            {isTopicDeleteModal && (
                <DeleteModal
                    isModal={isTopicDeleteModal}
                    setModal={setTopicDeleteModal}
                    deleteId={topicDeleteId}
                    handleDeleteData={deleteTopic}
                />
            )}
        </Wrapper>
    );
}

const MainContainer = styled.div``;

const LoadingContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 70dvh;
`;

const LoadingGif = styled.img`
    display: inline-block;
    width: 100px;
    @media (max-width: 980px) {
        width: 90px;
    }
`;

const TopTitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 30px;
`;

const TextDiv = styled.div`
    margin-bottom: 20px;
`;

const PathText = styled(Link)`
    display: inline-block;
    font-size: 14px;
    color: #9d9d9d;
    cursor: pointer;
    &:hover {
        text-decoration: underline;
    }
`;

const Slash = styled.span`
    display: inline-block;
    font-size: 14px;
    color: #9d9d9d;
    margin: 0 5px;
`;

const PathTextBlack = styled.span`
    color: #000;
    display: inline-block;
    font-size: 14px;
`;

const CourceMainContainer = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 20px;
`;

const Left = styled.div`
    width: ${({ hasTopics }) => (hasTopics ? "60%" : "100%")};
    transition: width 0.3s ease;
`;

const Right = styled.div`
    width: 40%;
`;

const Card = styled.div`
    padding: 20px;
    background: #fff;
    border-radius: 12px;
`;

const TopSection = styled.div`
    width: 100%;
    height: 320px;
    border-radius: 16px;
    margin-bottom: 20px;
`;

const ImageDiv = styled.div`
    width: 100%;
    height: 100%;

    img {
        width: 100%;
        height: 100%;
        object-fit: fill;
        border-radius: 12px;
    }
`;

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SikillBox = styled.div`
    background-color: #ffffff;
    padding: 5px;
    border-radius: 8px;
    border: 1px solid #cdd5df;
    font-size: 12px;
`;

const PublishToggle = styled.div`
    float: right;
    display: flex;
    align-items: center;
`;

const PublishText = styled.span`
    margin-right: 10px;
    color: #666;
`;

const ToggleRoutes = styled.input.attrs({ type: "checkbox" })`
    position: relative;
    width: 45px;
    height: 22px;
    appearance: none;
    background-color: ${(props) => (props.checked ? "#6e58b3" : "#ccc")};
    border-radius: 25px;
    outline: none;
    transition: background 0.3s;
    cursor: ${(props) => (props.checked ? "not-allowed" : "pointer")};

    &::after {
        content: "";
        position: absolute;
        top: 3px;
        left: ${(props) => (props.checked ? "27px" : "3px")};
        width: 16px;
        height: 16px;
        background-color: white;
        border-radius: 50%;
        transition: left 0.3s;
    }
`;

const TopicsHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`;

const TopicsTitle = styled.h3`
    font-size: 18px;
    font-weight: 600;
    color: #333;
`;

const AddTopicButton = styled.button`
    display: flex;
    align-items: center;
    padding: 8px 16px;
    background-color: #5a2d8f;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
`;

const AddIcon = styled.span`
    margin-right: 6px;
    font-size: 16px;
`;

const TopicsContainer = styled.div`
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #fff;
`;

const TopicItem = styled.div`
    border-bottom: 1px solid #e0e0e0;
    &:last-child {
        border-bottom: none;
    }
`;

const TopicHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    cursor: pointer;
`;

const TopicName = styled.div`
    font-weight: 500;
    width: 95%;
`;

const ExpandIcon = styled.div`
    font-size: 12px;
    color: #666;
`;

const TopicContent = styled.div`
    padding: 16px;
    border-top: 1px dashed #e0e0e0;
`;

const ContentButtons = styled.div`
    padding: 12px 14px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: #522f91;
    margin-bottom: 20px;
    cursor: pointer;
`;

const AddContentSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 20px;
    border: 1px dashed #ccc;
    border-radius: 8px;
`;

const AddContentText = styled.div`
    color: #666;
    font-size: 14px;
    margin-bottom: 16px;
    text-align: center;
`;

const AddContentButton = styled.button`
    display: flex;
    align-items: center;
    padding: 8px 16px;
    background-color: #5a2d8f;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
`;

// Content Item Components
const ContentItem = styled.div`
    padding: 16px;
    border-bottom: 1px solid #eaeaea;
    &:last-child {
        border-bottom: none;
    }
`;

const ContentRow = styled.div`
    display: flex;
    align-items: center;
    &:hover {
        /* background-color: #f9f9f9; */
    }
`;

const ContentThumbnail = styled.div`
    cursor: pointer;
    width: 120px;
    height: 80px;
    background-image: ${(props) =>
        props.bgImage ? `url(${props.bgImage})` : "none"};
    background-color: ${(props) => (props.bgImage ? "transparent" : "#f2f2f2")};
    background-size: cover;
    background-position: center;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 16px;
    transition: transform 0.3s ease;

    &:hover {
        transform: scale(1.05);
    }
`;

const PlayIcon = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    &:before {
        content: "▶";
        font-size: 16px;
    }
`;

const DocumentIcon = styled.div`
    width: 24px;
    height: 24px;
    &:before {
        content: "📄";
        font-size: 24px;
    }
`;

const ContentInfo = styled.div`
    flex: 1;
`;

const ContentLesson = styled.div`
    font-size: 12px;
    color: #666;
    margin-bottom: 6px;
`;

const ContentTitle = styled.div`
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 4px;
`;

const ContentDescription = styled.div`
    font-size: 14px;
    color: #777;
`;

const ContentActions = styled.div`
    margin-left: 16px;
`;

const ActionButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: #5a2d8f;
`;

const DeleteIcon = styled.span`
    display: inline-block;
    width: 20px;
    height: 20px;
    cursor: pointer;
    img {
        width: 100%;
        height: 100%;
        display: block;
    }
`;

const MainTitle = styled.h2`
    font-size: 28px;
    font-weight: 600;
    margin: 24px 0;
    color: #333;
    clear: both;
`;

const InstructorSection = styled.div`
    margin-bottom: 30px;
`;

const InstructorLabel = styled.div`
    color: #666;
    font-size: 14px;
    margin-bottom: 10px;
`;

const InstructorDetail = styled.div`
    display: flex;
    align-items: center;
`;

const InstructorAvatar = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #ddd;
    margin-right: 12px;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 50%;
    }
`;

const InstructorName = styled.div`
    font-weight: 500;
    color: #333;
`;

const EnrolledStudentsSection = styled.div`
    margin-top: 20px;
`;

// Content Detail View Components
const ContentDetailView = styled.div`
    width: 100%;
`;

const ContentDetailHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
`;

const BackButton = styled.button`
    background: none;
    border: none;
    color: #5a2d8f;
    font-weight: 500;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 0;
`;

const ContentTypeTag = styled.span`
    background-color: #f5f7fa;
    border: 1px solid #dbe3f1;
    border-radius: 20px;
    padding: 4px 12px;
    font-size: 12px;
    color: #333;
`;

const ContentDetailTitle = styled.div`
    font-size: 24px;
    font-weight: 600;
    margin: 24px 0;
    color: #333;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const VideoContainer = styled.div`
    width: 100%;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 24px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const VideoPlayer = styled.video`
    width: 100%;
    height: auto;
`;

const DocumentContainer = styled.div`
    width: 100%;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 24px;
    height: 500px;
`;

const ContentDetailDescription = styled.p`
    color: #666;
    line-height: 1.6;
    margin-top: 10px;
`;

const TabsContainer = styled.div`
    display: flex;
    border-bottom: 1px solid #e0e0e0;
    margin-bottom: 30px;
`;

const Tab = styled.div`
    padding: 12px 16px;
    cursor: pointer;
    font-weight: 500;
    color: ${(props) => (props.active ? "#6e58b3" : "#666")};
    border-bottom: ${(props) => (props.active ? "3px solid #6e58b3" : "none")};
    margin-right: 24px;
`;

const AboutSection = styled.div``;

const SectionTitle = styled.h3`
    font-size: 20px;
    font-weight: 600;
    margin: 24px 0 16px;
    color: #333;
`;

const Description = styled.p`
    color: #666;
    line-height: 1.6;
    margin-bottom: 30px;
`;

const SkillsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

const SkillBadge = styled.div`
    background-color: #f6f4ff;
    border: 1px solid #917afd;
    border-radius: 8px;
    padding: 8px 16px;
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 500;
    color: #404040;
`;

const CloseIcon = styled.span`
    margin-left: 8px;
    font-size: 18px;
    cursor: pointer;
`;
const MediaContainer = styled.div`
    width: 100%;
    position: relative;
    /* border-radius: 10px; */
    margin-bottom: 25px;
    .vid-player {
        height: auto !important;
        aspect-ratio: 16/9 !important;
        /* border-radius: 10px; */
    }
    &.player .vp-controls .logo {
        display: none !important;
    }
`;

const ContentEdit = styled.div`
    font-size: 14px;
    color: #522f91;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid #522e92;
    padding: 8px 16px;
    border-radius: 6px;
`;

// Student info related styled components
const StudentInfo = styled.div`
    display: flex;
    align-items: center;
`;

const StudentAvatar = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    overflow: hidden;
    margin-right: 10px;
`;

const AvatarImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;
